import { useState, useMemo, useEffect, useCallback } from 'react';
import { getAssignmentConflicts } from '../../../../utils/leaderUtils';

const STATUS_OPTIONS = [
    { value: 'aktiv', label: 'Aktiv', color: '#137333' },
    { value: 'pausiert', label: 'Pausiert', color: '#B06000' },
    { value: 'abgeschlossen', label: 'Abgeschlossen', color: '#1A73E8' }
];

/**
 * useListViewLogic - Custom hook for managing the state and logic of the ListView.
 * Handles filtering, sorting, searching, selection, and bulk actions.
 * 
 * @param {Object} params
 * @param {Array} params.assignments - List of all project assignments
 * @param {Array} params.workers - List of all workers
 * @param {Array} params.vehicles - List of all vehicles
 * @param {Array} params.kolonnen - List of all teams
 * @param {Array} params.assignmentsForConflicts - Base assignments for conflict detection
 * @param {Array} params.absencesForConflicts - Base absences for conflict detection
 * @param {Object} params.persistedState - Externally managed state for persistence
 * @param {Function} params.onAssignmentDelete - Callback to delete a single assignment
 * @param {Function} params.onExport - Callback to trigger PDF export
 */
export function useListViewLogic({
    assignments = [],
    workers = [],
    vehicles = [],
    kolonnen = [],
    assignmentsForConflicts = [],
    absencesForConflicts = [],
    persistedState,
    onAssignmentDelete,
    onExport
}) {
    // If persistedState is provided, use its values/setters. 
    // Otherwise fallback to internal state (for standalone usage if ever needed).
    const _searchTerm = useState('');
    const searchTerm = persistedState ? persistedState.searchTerm : _searchTerm[0];
    const setSearchTerm = persistedState ? persistedState.setSearchTerm : _searchTerm[1];

    const _statusFilter = useState(STATUS_OPTIONS.map(o => o.value));
    const statusFilter = persistedState ? persistedState.statusFilter : _statusFilter[0];
    const setStatusFilter = persistedState ? persistedState.setStatusFilter : _statusFilter[1];

    const _dateRange = useState({ start: null, end: null });
    const dateRange = persistedState ? persistedState.dateRange : _dateRange[0];
    const setDateRange = persistedState ? persistedState.setDateRange : _dateRange[1];

    const _dateMode = useState('combined');
    const dateMode = persistedState ? persistedState.dateMode : _dateMode[0];
    const setDateMode = persistedState ? persistedState.setDateMode : _dateMode[1];

    const [timeRange, setTimeRange] = useState({ start: 0, end: 24, showAllDay: true });
    
    const _workerFilter = useState([]);
    const workerFilter = persistedState ? persistedState.workerFilter : _workerFilter[0];
    const setWorkerFilter = persistedState ? persistedState.setWorkerFilter : _workerFilter[1];

    const _vehicleFilter = useState([]);
    const vehicleFilter = persistedState ? persistedState.vehicleFilter : _vehicleFilter[0];
    const setVehicleFilter = persistedState ? persistedState.setVehicleFilter : _vehicleFilter[1];

    const _kolonnenFilter = useState([]);
    const kolonnenFilter = persistedState ? persistedState.kolonnenFilter : _kolonnenFilter[0];
    const setKolonnenFilter = persistedState ? persistedState.setKolonnenFilter : _kolonnenFilter[1];
    
    const _workerSearch = useState('');
    const workerSearch = persistedState ? persistedState.workerSearch : _workerSearch[0];
    const setWorkerSearch = persistedState ? persistedState.setWorkerSearch : _workerSearch[1];

    const _vehicleSearch = useState('');
    const vehicleSearch = persistedState ? persistedState.vehicleSearch : _vehicleSearch[0];
    const setVehicleSearch = persistedState ? persistedState.setVehicleSearch : _vehicleSearch[1];

    const _kolonneSearch = useState('');
    const kolonneSearch = persistedState ? persistedState.kolonneSearch : _kolonneSearch[0];
    const setKolonneSearch = persistedState ? persistedState.setKolonneSearch : _kolonneSearch[1];
    
    const _openDropdown = useState(null);
    const openDropdown = persistedState ? persistedState.openDropdown : _openDropdown[0];
    const setOpenDropdown = persistedState ? persistedState.setOpenDropdown : _openDropdown[1];

    const _sortConfig = useState({ key: 'start_datetime', direction: 'desc' });
    const sortConfig = persistedState ? persistedState.sortConfig : _sortConfig[0];
    const setSortConfig = persistedState ? persistedState.setSortConfig : _sortConfig[1];

    const _selectedIds = useState([]);
    const selectedIds = persistedState ? persistedState.selectedIds : _selectedIds[0];
    const setSelectedIds = persistedState ? persistedState.setSelectedIds : _selectedIds[1];

    const _hidePast = useState(true);
    const hidePast = persistedState ? persistedState.hidePast : _hidePast[0];
    const setHidePast = persistedState ? persistedState.setHidePast : _hidePast[1];

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Initialize multi-selects to "All" when data arrives
    useEffect(() => {
        if (workers.length > 0 && workerFilter.length === 0) {
            setWorkerFilter(workers.map(w => w.id));
        }
    }, [workers]);

    useEffect(() => {
        if (vehicles.length > 0 && vehicleFilter.length === 0) {
            setVehicleFilter(vehicles.map(v => v.id));
        }
    }, [vehicles]);

    useEffect(() => {
        if (kolonnen.length > 0 && kolonnenFilter.length === 0) {
            setKolonnenFilter([...kolonnen.map(k => k.id), 'none']);
        }
    }, [kolonnen]);

    const isFilterActive = useMemo(() => {
        const statusActive = statusFilter.length < STATUS_OPTIONS.length;
        const dateActive = !!(dateRange.start || dateRange.end) || dateMode !== 'combined';
        const timeActive = timeRange.start > 0 || timeRange.end < 24 || !timeRange.showAllDay;
        const workerActive = workers.length > 0 && workerFilter.length < workers.length;
        const vehicleActive = vehicles.length > 0 && vehicleFilter.length < vehicles.length;
        const kolonnenActive = kolonnen.length > 0 && kolonnenFilter.length < (kolonnen.length + 1);
        const searchActive = !!searchTerm;
        
        return statusActive || dateActive || timeActive || workerActive || vehicleActive || kolonnenActive || searchActive;
    }, [statusFilter, dateRange, dateMode, timeRange, workerFilter, vehicleFilter, kolonnenFilter, searchTerm, workers.length, vehicles.length, kolonnen.length]);

    const resetAllFilters = () => {
        setStatusFilter(STATUS_OPTIONS.map(o => o.value));
        setDateRange({ start: null, end: null });
        setDateMode('combined');
        setTimeRange({ start: 0, end: 24, showAllDay: true });
        setWorkerFilter(workers.map(w => w.id));
        setVehicleFilter(vehicles.map(v => v.id));
        setKolonnenFilter([...kolonnen.map(k => k.id), 'none']);
        setSearchTerm('');
        setOpenDropdown(null);
        setSelectedIds([]);
        setHidePast(true);
    };

    const filtered = useMemo(() => {
        let result = assignments || [];

        // 1. Status filter
        if (statusFilter.length < STATUS_OPTIONS.length) {
            result = result.filter(a => statusFilter.includes(a.status || 'aktiv'));
        }

        // 2. Date Filter
        result = result.filter(a => {
            const hasDate = !!a.start_datetime;
            if (dateMode === 'undated') return !hasDate;
            if (dateMode === 'calendar' && !hasDate) return false;
            if (!hasDate) return true; // combined

            // Hide past logic
            if (hidePast) {
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                const aEnd = new Date(a.end_datetime || a.start_datetime);
                aEnd.setHours(23, 59, 59, 999);
                if (aEnd < now) return false;
            }

            if (dateRange.start || dateRange.end) {
                const aStart = new Date(a.start_datetime);
                const aEnd = new Date(a.end_datetime || a.start_datetime);
                aStart.setHours(0,0,0,0);
                aEnd.setHours(23,59,59,999);

                if (dateRange.start) {
                    const dStart = new Date(dateRange.start);
                    dStart.setHours(0,0,0,0);
                    if (aEnd < dStart) return false;
                }
                if (dateRange.end) {
                    const dEnd = new Date(dateRange.end);
                    dEnd.setHours(23,59,59,999);
                    if (aStart > dEnd) return false;
                }
            }
            return true;
        });

        // 3. Time Filter
        const isTimeFiltered = timeRange.start > 0 || timeRange.end < 24 || !timeRange.showAllDay;
        if (isTimeFiltered) {
            result = result.filter(a => {
                const hasStart = !!a.start_datetime;
                if (!hasStart) return timeRange.showAllDay;
                const s = new Date(a.start_datetime);
                const e = a.end_datetime ? new Date(a.end_datetime) : s;
                const isAllDay = s.getHours() === 0 && s.getMinutes() === 0 && e.getHours() === 0 && e.getMinutes() === 0;
                if (isAllDay) return timeRange.showAllDay;
                return s.getHours() < timeRange.end && (e.getHours() + (e.getMinutes() > 0 ? 1 : 0)) > timeRange.start;
            });
        }

        // 4. Worker filter
        if (workerFilter.length < workers.length) {
            result = result.filter(a => (a.workers || []).some(w => workerFilter.includes(w.worker_id)));
        }

        // 5. Vehicle filter
        if (vehicleFilter.length < vehicles.length) {
            result = result.filter(a => (a.vehicles || []).some(v => vehicleFilter.includes(v.vehicle_id)));
        }

        // 6. Kolonnen filter
        if (kolonnenFilter.length < (kolonnen.length + 1)) {
            result = result.filter(a => {
                const aKolonnen = [...new Set([
                    ...(a.workers || []).map(w => w.kolonne_id),
                    ...(a.vehicles || []).map(v => v.kolonne_id)
                ])].filter(Boolean);
                if (aKolonnen.length === 0) return kolonnenFilter.includes('none');
                return aKolonnen.some(id => kolonnenFilter.includes(id));
            });
        }

        // 7. Search
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(a => {
                const workersStr = (a.workers || []).map(w => w.worker_name || '').join(' ').toLowerCase();
                const vehiclesStr = (a.vehicles || []).map(v => v.license_plate || '').join(' ').toLowerCase();
                return (
                    (a.project_name || '').toLowerCase().includes(lower) ||
                    (a.location || '').toLowerCase().includes(lower) ||
                    (a.notes || '').toLowerCase().includes(lower) ||
                    workersStr.includes(lower) ||
                    vehiclesStr.includes(lower)
                );
            });
        }

        return result;
    }, [assignments, statusFilter, searchTerm, dateRange, dateMode, timeRange, workerFilter, vehicleFilter, kolonnenFilter, workers.length, vehicles.length, kolonnen.length, hidePast]);

    const sorted = useMemo(() => {
        if (!sortConfig.key) return filtered;
        return [...filtered].sort((a, b) => {
            let aVal, bVal;
            switch (sortConfig.key) {
                case 'project_name':
                case 'location':
                case 'notes':
                    aVal = (a[sortConfig.key] || '').toLowerCase();
                    bVal = (b[sortConfig.key] || '').toLowerCase();
                    break;
                case 'start_datetime':
                    aVal = a.start_datetime ? new Date(a.start_datetime).getTime() : 0;
                    bVal = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
                    break;
                case 'kolonne':
                    // Get first kolonne name for sorting
                    aVal = [...new Set([
                        ...(a.workers || []).map(w => w.kolonne_id),
                        ...(a.vehicles || []).map(v => v.kolonne_id)
                    ])].filter(Boolean).map(id => kolonnen.find(k => k.id === id)?.name || '').join(', ');
                    bVal = [...new Set([
                        ...(b.workers || []).map(w => w.kolonne_id),
                        ...(b.vehicles || []).map(v => v.kolonne_id)
                    ])].filter(Boolean).map(id => kolonnen.find(k => k.id === id)?.name || '').join(', ');
                    break;
                case 'conflicts':
                    aVal = getAssignmentConflicts(a, assignmentsForConflicts, absencesForConflicts, vehicles).total;
                    bVal = getAssignmentConflicts(b, assignmentsForConflicts, absencesForConflicts, vehicles).total;
                    break;
                case 'status':
                    aVal = a.status || 'aktiv';
                    bVal = b.status || 'aktiv';
                    break;
                case 'workers':
                    aVal = (a.workers || []).length;
                    bVal = (b.workers || []).length;
                    break;
                case 'vehicles':
                    aVal = (a.vehicles || []).length;
                    bVal = (b.vehicles || []).length;
                    break;
                default:
                    aVal = a[sortConfig.key] || '';
                    bVal = b[sortConfig.key] || '';
            }
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filtered, sortConfig]);

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleSelectAll = (checked) => {
        setSelectedIds(checked ? sorted.map(a => a.id) : []);
    };

    /**
     * Handlers for bulk and single interactions
     */
    const handleDeleteAssignment = useCallback((id) => {
        if (window.confirm('Diesen Einsatz wirklich löschen?')) {
            onAssignmentDelete(id);
        }
    }, [onAssignmentDelete]);

    const handleBulkDelete = useCallback(() => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`${selectedIds.length} Einsätze wirklich löschen?`)) return;
        selectedIds.forEach(id => onAssignmentDelete(id));
        setSelectedIds([]);
    }, [selectedIds, onAssignmentDelete, setSelectedIds]);

    const handleBulkExport = useCallback(() => {
        if (selectedIds.length === 0) return;
        onExport({
            status: statusFilter,
            dateRange: dateRange,
            dateMode: dateMode,
            worker: workerFilter,
            vehicle: vehicleFilter,
            kolonne: kolonnenFilter,
            search: searchTerm
        }, selectedIds);
    }, [onExport, selectedIds, statusFilter, dateRange, dateMode, workerFilter, vehicleFilter, kolonnenFilter, searchTerm]);

    const handleExport = useCallback(() => {
        onExport({
            status: statusFilter,
            dateRange: dateRange,
            dateMode: dateMode,
            worker: workerFilter,
            vehicle: vehicleFilter,
            kolonne: kolonnenFilter,
            search: searchTerm
        });
    }, [onExport, statusFilter, dateRange, dateMode, workerFilter, vehicleFilter, kolonnenFilter, searchTerm]);

    return {
        searchTerm, setSearchTerm,
        statusFilter, setStatusFilter,
        dateRange, setDateRange,
        dateMode, setDateMode,
        timeRange, setTimeRange,
        workerFilter, setWorkerFilter,
        vehicleFilter, setVehicleFilter,
        kolonnenFilter, setKolonnenFilter,
        workerSearch, setWorkerSearch,
        vehicleSearch, setVehicleSearch,
        kolonneSearch, setKolonneSearch,
        openDropdown, setOpenDropdown,
        sortConfig, handleSort,
        selectedIds, setSelectedIds,
        toggleSelect, toggleSelectAll,
        hidePast, setHidePast,
        isFilterActive, resetAllFilters,
        sorted, filtered,
        STATUS_OPTIONS,
        handleDeleteAssignment,
        handleBulkDelete,
        handleBulkExport,
        handleExport
    };
}
